/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/reviewComponent.scss';

import React, {useContext, useMemo} from 'react';
import ProductReview from "../../models/productReviewModel";
import BolRating from "../bolRating/bolRating";
import RouterLink from "../controls/link/routerLink";
import Image from "../misc/image";
import IDate from '@dvrd/idate';
import {AppContext} from "../misc/appContext";
import {LAST_REVIEW_FLEX_KEY} from "../../util/constants";
import {AwesomeIcon, stopPropagation} from '@dvrd/dvr-controls';
import classNames from 'classnames';

interface Props {
    review: ProductReview;
    withLinks?: boolean;
    className?: string;
}

export default function ReviewComponent(props: Props) {
    const {account} = useContext(AppContext).accountContext;
    const {review, withLinks, className} = props;
    const [dayText, isNew] = useMemo(() => {
        const lastSeen = account?.getFlex(LAST_REVIEW_FLEX_KEY);
        const today = IDate.now();
        const reviewDate = review.reviewDate;
        const isNew = new IDate(lastSeen).isBefore(review.createdAtMoment);
        let dayText: string;
        const diffInDays = reviewDate.absDiff(today, 'days')
        if (reviewDate.isAt(today, 'day'))
            dayText = 'Vandaag';
        else if (diffInDays === 1)
            dayText = 'Gisteren';
        else dayText = `${diffInDays} dagen geleden`;
        return [dayText, isNew];
    }, [account?.id]);

    function renderHeader() {
        return (
            <div className='review-header'>
                <div className='title-row'>
                    <BolRating value={review.rating / 20}/>
                    <label className='review-title'>
                        {review.reviewTitle}
                    </label>
                    <div className='date-container'>
                        {isNew && <div className='new-tag'>
                            <AwesomeIcon name='eye' prefix='far'/>
                            <span>Nieuw</span>
                        </div>}
                        <label className='day-text'>{dayText}</label>
                    </div>
                </div>
                <div className='subtitle-row'>
                    {renderAuthor()}
                    {!!withLinks && (
                        <div className='product-links'>
                            <RouterLink to={`/review-analytics/${review.ean}`}
                                        title='Alle reviews van dit product bekijken en analyseren'
                                        onClick={stopPropagation}>
                                <Image name='logo-no-text' alt='' className='logo-link'/>
                            </RouterLink>
                            <RouterLink
                                to={`https://www.bol.com/nl/nl/p/${review.title?.replace('/',
                                    '-')}/${review.globalId}#product-reviews`}
                                target='_blank' title='Bekijk product op bol' onClick={stopPropagation}>
                                <Image name='bol-fav-32' alt='' className='logo-link'/>
                            </RouterLink>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    function renderAuthor() {
        const knownData: Array<string> = [];
        if (review.author) knownData.push(review.author);
        if (review.age) knownData.push(review.age);
        if (review.city) knownData.push(review.city);
        knownData.push(review.reviewDate.format(ProductReview.dateFormat));
        return <label className='subtitle'>{knownData.join(' | ')}</label>
    }

    function renderProsCons() {
        if (!review.pros.length && !review.cons.length) return null;
        return (
            <div className='pros-cons-container'>
                {!!review.pros.length && <div className='pros-cons'>
                    {review.pros.map(renderProCon(true))}
                </div>}
                {!!review.cons.length && <div className='pros-cons'>
                    {review.cons.map(renderProCon(false))}
                </div>}
            </div>
        )
    }

    function renderProCon(isPro: boolean) {
        return function(proCon: string, index: number) {
            const icon = isPro ? 'plus' : 'minus';
            return (
                <div key={index} className='pro-con'>
                    <AwesomeIcon name={icon} className={classNames('pro-con-icon', icon)}/>
                    <label>{proCon}</label>
                </div>
            )
        }
    }

    function renderFeedback() {
        return (
            <div className='feedback-container'>
                <div className='feedback-info'>
                    <AwesomeIcon name='thumbs-up' prefix='far'/>
                    <label>{review.numPositive}</label>
                </div>
                <div className='feedback-info'>
                    <AwesomeIcon name='thumbs-down' prefix='far'/>
                    <label>{review.numNegative}</label>
                </div>
            </div>
        )
    }

    return (
        <div className={classNames('review-component', className)} id={`review-${review.id}`}>
            {renderHeader()}
            {renderProsCons()}
            <label className='review-body'>
                {review.body}
            </label>
            {renderFeedback()}
        </div>
    )
}